import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Search, Package } from 'lucide-react';

interface Pack {
  id: number;
  name: string;
  retailerName?: string;
}

export default function PackLockingSelector() {
  const [, setLocation] = useLocation();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);

  // Check feature flag on component mount
  useEffect(() => {
    const checkFeatureFlag = async () => {
      try {
        const response = await fetch('/api/_health/pack-lock-preview');
        setFeatureEnabled(response.ok);
      } catch (error) {
        setFeatureEnabled(false);
      }
    };
    
    checkFeatureFlag();
  }, []);

  // Fetch packs on component mount
  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const response = await fetch('/api/retailers');
        if (response.ok) {
          const retailers = await response.json();
          const allPacks: Pack[] = [];
          
          // Fetch packs for each retailer
          for (const retailer of retailers) {
            const packsResponse = await fetch(`/api/retailers/${retailer.id}/packs`);
            if (packsResponse.ok) {
              const retailerPacks = await packsResponse.json();
              allPacks.push(...retailerPacks.map((pack: any) => ({
                ...pack,
                retailerName: retailer.name
              })));
            }
          }
          
          setPacks(allPacks);
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  const filteredPacks = packs.filter(pack =>
    pack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pack.retailerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePackSelect = (packId: number) => {
    setLocation(`/pack-locking/${packId}`);
  };

  // Show feature disabled message if feature flag is off
  if (featureEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Lock className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Feature Disabled</h2>
          <p className="text-muted-foreground mb-4">
            Pack Locking & Preview feature is currently disabled by server configuration.
          </p>
          <Button onClick={() => setLocation('/pack-management')}>
            Go to Pack Management
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Package className="h-12 w-12 mx-auto text-blue-500 mb-4 animate-spin" />
          <p>Loading packs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
            <Lock className="h-8 w-8" />
            Pack Locking & Preview
          </h1>
          <p className="text-muted-foreground mt-2">
            Select a pack to access Pack Locking, Preview, and Campaign Simulation features
          </p>
        </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Packs
            </CardTitle>
            <CardDescription>
              Find packs by name or retailer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search by pack name or retailer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packs List */}
        <Card>
          <CardHeader>
            <CardTitle>Available Packs</CardTitle>
            <CardDescription>
              {filteredPacks.length} pack{filteredPacks.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredPacks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4" />
                <p>No packs found matching your search.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredPacks.map((pack) => (
                  <div
                    key={pack.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <h3 className="font-medium">{pack.name}</h3>
                      {pack.retailerName && (
                        <p className="text-sm text-muted-foreground">
                          Retailer: {pack.retailerName}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => handlePackSelect(pack.id)}
                      className="flex items-center gap-2"
                    >
                      <Lock className="h-4 w-4" />
                      Open Pack Locking
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setLocation('/pack-management')}
          >
            Back to Pack Management
          </Button>
        </div>
      </div>
    </div>
  );
}
